import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, Shield, Wrench, UserCircle } from "lucide-react";

export function UserManagement() {
  const { user, getUsers, createUser, deleteUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "mechanic" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (newUser.password.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }
    setCreating(true);
    try {
      await createUser(newUser.username, newUser.password, newUser.role);
      toast.success(`Utilisateur ${newUser.username} créé avec succès`);
      setNewUser({ username: "", password: "", role: "mechanic" });
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${username}" ?`)) {
      return;
    }
    try {
      await deleteUser(userId);
      toast.success(`Utilisateur ${username} supprimé`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <Shield className="w-4 h-4 text-primary" />;
      case "mechanic":
        return <Wrench className="w-4 h-4 text-blue-500" />;
      default:
        return <UserCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "mechanic":
        return "Mécanicien";
      case "staff":
        return "Mécanicien";
      default:
        return role;
    }
  };

  if (!isAdmin()) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse text-muted-foreground">Chargement...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="user-management">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <CardTitle className="font-heading">Gestion des utilisateurs</CardTitle>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="btn-add-user">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un nouveau compte</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-username">Nom d'utilisateur</Label>
                <Input
                  id="new-username"
                  placeholder="Nom d'utilisateur"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  data-testid="input-new-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Mot de passe"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="input-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-role">Rôle</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="select-role">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mechanic">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        Mécanicien
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Administrateur
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Les mécaniciens n'ont pas accès aux prix et ne peuvent pas supprimer de scooters.
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={creating} data-testid="btn-create-user">
                {creating ? "Création..." : "Créer le compte"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {users.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border"
              data-testid={`user-row-${u.username}`}
            >
              <div className="flex items-center gap-3">
                {getRoleIcon(u.role)}
                <div>
                  <p className="font-medium">{u.username}</p>
                  <p className="text-xs text-muted-foreground">{getRoleName(u.role)}</p>
                </div>
              </div>
              {u.id !== user?.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteUser(u.id, u.username)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`btn-delete-user-${u.username}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {users.length === 0 && (
            <p className="text-center text-muted-foreground py-4">
              Aucun utilisateur trouvé
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
